/**
 * Juftlik tekshiruvi — imtiyoz arizasidan (expertiza) kelgan so'rovlar.
 *
 * TIF TN tekshiruvchisi tovarga belgi qo'yganda tovar QAROR BILAN BIRGA
 * `/api/verify` ga keladi. AI qarorni ko'rmaydi — faqat nom va kod bo'yicha
 * o'z xulosasini chiqaradi. Bu yorliq ikkalasini yonma-yon ko'rsatadi.
 *
 * Solishtirish «aqlli»: tekshiruvchining «xato» izohi tasniflanadi (kod xato /
 * kod to'g'ri, lekin ma'lumot yetishmaydi / faqat ma'lumot yetishmaydi /
 * kodga aloqasiz) va natija shunga qarab hisoblanadi (`outcome`, backend).
 * Ko'rsatkichlar har tovarning OXIRGI so'rovi bo'yicha.
 */

import { useEffect, useState } from "react";
import * as api from "../../api/client";
import { Alert, Chip, Filters, List, Pager, Panel, Search, Stat, Stats } from "./ui";
import { agreement, code, usePaged, when } from "./lib";

const PAGE = 25;

const VERDICT = {
  correct: ["To'g'ri", "border-emerald-800/50 bg-emerald-950/50 text-emerald-200"],
  wrong: ["Xato", "border-rose-800/50 bg-rose-950/50 text-rose-200"],
  unsure: ["Noaniq", "border-amber-800/50 bg-amber-950/50 text-amber-200"],
};

const OUTCOME = {
  agree: ["mos", "border-emerald-800/50 bg-emerald-950/40 text-emerald-300"],
  partial: ["qisman", "border-sky-700/50 bg-sky-950/60 text-sky-200"],
  disagree: ["mos emas", "border-rose-800/50 bg-rose-950/40 text-rose-300"],
  ai_unsure: ["AI noaniq", "border-amber-800/50 bg-amber-950/40 text-amber-300"],
  excluded: ["solishtirilmaydi", "border-sky-900/50 bg-[#041228] text-sky-400/70"],
};

// Tekshiruvchi «xato» izohi nimani aytadi (`human_reason.about_code`)
const ABOUT = {
  wrong: "kod xato",
  correct: "kod to'g'ri",
  unclear: "kod haqida hukm yo'q",
  not_about_code: "kodga aloqasiz",
};

const CORE = {
  match: "mos",
  uncertain: "shubhali",
  mismatch: "mos emas",
  code_not_found: "kod nashrda yo'q",
};

const TRIGGER = {
  tnved_mark: "tekshiruvchi belgisi",
  backfill: "eski belgi (backfill)",
  manual_test: "qo'lda sinov",
};

const FILTERS = [
  ["", "Hammasi"],
  ["agree", "Mos keldi"],
  ["partial", "Qisman"],
  ["disagree", "Mos kelmadi"],
  ["ai_unsure", "AI noaniq"],
  ["hard", "Aniqlash qiyin"],
  ["excluded", "Solishtirilmaydi"],
  ["no_human", "Tekshiruvchisiz"],
  ["failed", "AI javob bermadi"],
];

const SUPPORT = {
  true: ["tasdiqlaydi", "text-emerald-300"],
  false: ["shubha", "text-rose-300"],
  null: ["ma'lumot", "text-sky-400/70"],
};

function VerdictChip({ who, verdict }) {
  const [label, cls] = VERDICT[verdict] ?? [verdict || "—", undefined];
  return <Chip cls={cls}>{who}: {label}</Chip>;
}

const AMBER = "border-amber-800/50 bg-amber-950/40 text-amber-300";

export default function VerifyTab({ days, refresh, preset = "" }) {
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState("");
  const [kind, setKind] = useState(preset);
  const [q, setQ] = useState("");
  const [tests, setTests] = useState(false);
  const [history, setHistory] = useState(false);

  useEffect(() => {
    setStatsErr("");
    api.verifyStats({ tests, days }).then(setStats, (e) => setStatsErr(e.message || String(e)));
  }, [tests, days, refresh]);

  const list = usePaged(
    (page) => api.verifyTurns({ limit: PAGE, offset: page * PAGE, q, kind, tests, history, days }),
    [kind, q, tests, history, days],
    refresh,
  );

  const { compared, pct: agreePct } = agreement(stats);

  return (
    <div className="space-y-5">
      <Alert>{statsErr || list.error}</Alert>

      {stats && (
        <Stats>
          <Stat label="Tovarlar" value={stats.goods} hint={`${stats.applications} ariza · ${stats.total} so'rov`} />
          <Stat
            label="Gemini"
            value={stats.llm}
            hint={
              stats.llm
                ? `o'rtacha ${(stats.llm_avg_ms / 1000).toFixed(1)} s · ${stats.reused} qayta`
                : "chaqirilmagan"
            }
          />
          <Stat
            label="Mos keldi"
            value={agreePct === null ? "—" : `${agreePct}%`}
            hint={`${stats.agree} mos · ${stats.partial} qisman · ${compared} dan`}
            tone={agreePct === null ? "text-sky-400/50" : "text-emerald-300"}
          />
          <Stat label="Mos kelmadi" value={stats.disagree} tone={stats.disagree > 0 ? "text-rose-300" : ""} />
          <Stat
            label="AI noaniq"
            value={stats.ai_unsure}
            hint={stats.excluded ? `${stats.excluded} ta kodga aloqasiz izoh` : "tekshiruvchi aniq qaror bergan"}
            tone={stats.ai_unsure > 0 ? "text-amber-300" : ""}
          />
          <Stat
            label="Aniqlash qiyin"
            value={stats.hard}
            hint={
              stats.unclassified
                ? `${stats.unclassified} izoh tasniflanmagan`
                : stats.failed
                  ? `${stats.failed} ta AI javob bermadi`
                  : "ma'lumot yetishmaydi"
            }
            tone={stats.hard > 0 ? "text-amber-300" : ""}
          />
        </Stats>
      )}

      <Filters items={FILTERS} value={kind} onChange={setKind} />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] text-sky-300/80">
          <input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} className="accent-sky-500" />
          Tarix (har tovarning hamma so'rovlari)
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-sky-300/80">
          <input type="checkbox" checked={tests} onChange={(e) => setTests(e.target.checked)} className="accent-sky-500" />
          Sinov yozuvlari
        </label>
        <Search placeholder="Nom, kod yoki ariza raqami..." onSearch={setQ} />
      </div>

      <List
        loading={list.loading}
        items={list.items}
        empty={
          stats?.total === 0 && !kind && !q
            ? "Hali so'rov kelmagan — tekshiruvchi belgi qo'yganda shu yerda paydo bo'ladi"
            : ""
        }
      >
        <div className="space-y-3">
          {list.items.map((t) => (
            <TurnCard key={t.id} turn={t} />
          ))}
        </div>
      </List>

      <Pager page={list.page} setPage={list.setPage} full={list.items.length === PAGE} />
    </div>
  );
}

function TurnCard({ turn }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const [err, setErr] = useState("");

  const human = turn.human_verdict;
  const reason = turn.human_reason;
  const meta = turn.meta ?? {};
  const outcome = OUTCOME[turn.outcome];
  const missing = turn.ai_missing ?? [];

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !full) {
      try {
        setFull(await api.verifyTurn(turn.id));
      } catch (e) {
        setErr(e.message || String(e));
      }
    }
  };

  return (
    <div className="rounded-xl border border-sky-800/40 bg-[#030f24]/70 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded border border-sky-800/50 bg-sky-950/80 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-sky-200">
              {code(turn.code)}
            </span>
            {human ? (
              <VerdictChip who="Tekshiruvchi" verdict={human} />
            ) : (
              <Chip cls="border-sky-900/50 bg-[#041228] text-sky-400/60">Tekshiruvchi: —</Chip>
            )}
            {reason && <Chip>{ABOUT[reason.about_code] ?? reason.about_code}</Chip>}
            {reason?.info_missing && <Chip cls={AMBER}>ma'lumot yetishmaydi</Chip>}
            {turn.status === "failed" ? (
              <Chip cls="border-rose-800/50 bg-rose-950/50 text-rose-300">AI javob bermadi</Chip>
            ) : (
              <VerdictChip who="AI" verdict={turn.verdict} />
            )}
            {outcome && <Chip cls={outcome[1]}>{outcome[0]}</Chip>}
            {turn.reused_from && <Chip>qayta ishlatilgan</Chip>}
            {turn.warning && <Chip cls={AMBER}>ogohlantirish</Chip>}
          </div>
          <div className="mt-1.5 text-xs font-medium text-white">{turn.name}</div>
          <div className="mt-0.5 text-[10px] text-sky-400/60">
            Ariza {meta.application_number || turn.application_id}
            {meta.good_sort != null && ` · tovar № ${meta.good_sort}`}
            {" · "}
            {TRIGGER[turn.trigger] ?? turn.trigger}
          </div>
        </div>
        <div className="shrink-0 text-right font-mono text-[10px] text-sky-400/50">
          <div>{when(turn.created_at)}</div>
          <div>{turn.ms >= 1000 ? `${(turn.ms / 1000).toFixed(1)} s` : `${turn.ms} ms`}</div>
        </div>
      </div>

      <button
        onClick={toggle}
        aria-expanded={open}
        className="mt-2 text-[11px] text-sky-300 transition-colors hover:text-white"
      >
        {open ? "Yashirish" : "Batafsil"}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Panel title="Tekshiruvchi qarori">
              {human ? (
                <>
                  <VerdictChip who="Belgi" verdict={human} />
                  {reason?.summary && <p className="mt-1.5 text-sky-100">{reason.summary}</p>}
                  {reason?.proposed_code && (
                    <p className="mt-1 text-[11px]">
                      Tekshiruvchi taklif qilgan kod:{" "}
                      <span className="font-mono text-emerald-300">{code(reason.proposed_code)}</span>
                    </p>
                  )}
                  <p className="mt-1.5 whitespace-pre-wrap text-sky-300/70">
                    {turn.human_note || "(izohsiz)"}
                  </p>
                  <p className="mt-1.5 text-[10px] text-sky-400/60">
                    Xodim #{turn.human_by ?? "—"} · {when(turn.human_at)}
                  </p>
                </>
              ) : (
                <p className="text-sky-400/60">Qaror yuborilmagan</p>
              )}
            </Panel>
            <Panel title="AI xulosasi">
              {turn.status === "failed" ? (
                <p className="text-rose-300">{turn.error || "AI javob bermadi"}</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    <VerdictChip who="Gemini" verdict={turn.verdict} />
                    <Chip>Yadro: {CORE[turn.core_verdict] ?? turn.core_verdict}</Chip>
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap">{turn.comment || "(izohsiz)"}</p>
                  {missing.length > 0 && (
                    <p className="mt-1.5 text-[11px] text-amber-300">
                      Yetishmayotgan ma'lumot: {missing.join(", ")}
                    </p>
                  )}
                  {turn.suggested?.length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      <div className="text-[10px] text-sky-400/60">Taklif qilingan kodlar:</div>
                      {turn.suggested.map((s) => (
                        <div key={s.code} className="text-[11px]">
                          <span className="font-mono text-sky-200">{code(s.code)}</span>{" "}
                          <span className="text-sky-300/70">{s.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-1.5 text-[10px] text-sky-400/60">
                    {turn.model || "qoida (LLM'siz)"}
                    {turn.reused_from && ` · natija ${turn.reused_from.slice(0, 8)} dan`}
                  </p>
                </>
              )}
              {turn.warning && <p className="mt-1.5 text-[10px] text-amber-300">{turn.warning}</p>}
            </Panel>
          </div>

          {err && <div className="text-[11px] text-rose-300">{err}</div>}
          {!full && !err && <div className="text-[11px] text-sky-400/60">yuklanmoqda...</div>}
          {full && (
            <>
              {full.evidence?.length > 0 && (
                <Panel title="Yadro dalillari">
                  <ul className="space-y-1">
                    {full.evidence.map((e, i) => {
                      const [label, cls] = SUPPORT[String(e.supports)] ?? SUPPORT.null;
                      return (
                        <li key={i}>
                          <span className={`${cls} text-[10px]`}>[{label}]</span> {e.summary}
                        </li>
                      );
                    })}
                  </ul>
                </Panel>
              )}
              <Raw title="App yuborgan so'rov (to'liq)" text={JSON.stringify(full.request, null, 2)} />
              {full.context && <Raw title="Gemini'ga berilgan matn" text={full.context} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Raw({ title, text }) {
  return (
    <details className="rounded-lg border border-sky-900/50 bg-[#041228]/60 p-2.5">
      <summary className="cursor-pointer text-[11px] text-sky-300 hover:text-white">{title}</summary>
      <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-slate-300">
        {text}
      </pre>
    </details>
  );
}
